import React from 'react';

import BottomSheetModal from './BottomSheetModal';
import './EditModal.css';

/**
 * Edit Modal Component
 * Complex form for editing parameters with special modifier handling
 */
const EditModal = ({ 
  isOpen, 
  onClose, 
  editingItem = null,
  onSave,
  isLoading = false
}) => {
  const [item, setItem] = React.useState(editingItem);
  
  // Update local state when prop changes
  React.useEffect(() => {
    setItem(editingItem);
  }, [editingItem]);
  
  const handleInputChange = (field, value) => {
    setItem(prev => ({ ...prev, [field]: value }));
  };
  
  const handleLevelChange = (levelIndex, field, value) => {
    if (!item.levels) return;
    
    const newLevels = [...item.levels];
    newLevels[levelIndex] = {
      ...newLevels[levelIndex],
      [field]: value
    };
    
    setItem(prev => ({ ...prev, levels: newLevels }));
  };
  
  const handleSave = () => {
    if (!item.name.trim()) {
      alert('Name is required');
      return;
    }
    onSave(item);
    onClose();
  };
  
  const handleCancel = () => {
    setItem(editingItem); // Reset to original values
    onClose();
  };
  
  // Helper functions for modifier level display names
  const getLevelDisplayNameFromString = (modifierName, levelName) => {
    const levelMappings = {
      'assertiveness': {
        'most_collaborative': 'Most Collaborative',
        'more_collaborative': 'More Collaborative',
        'collaborative': 'Collaborative',
        'decisive': 'Decisive',
        'more_decisive': 'More Decisive',
        'most_decisive': 'Most Decisive'
      },
      'length': {
        'most_concise': 'Most Concise',
        'more_concise': 'More Concise',
        'concise': 'Concise',
        'detailed': 'Detailed',
        'more_detailed': 'More Detailed',
        'most_detailed': 'Most Detailed'
      },

      'directness': {
        'most_diplomatic': 'Most Diplomatic',
        'more_diplomatic': 'More Diplomatic',
        'diplomatic': 'Diplomatic',
        'blunt': 'Blunt',
        'more_blunt': 'More Blunt',
        'most_blunt': 'Most Blunt'
      },
      'energy': {
        'most_calm': 'Most Calm',
        'more_calm': 'More Calm',
        'calm': 'Calm',
        'excited': 'Excited',
        'more_excited': 'More Excited',
        'most_excited': 'Most Excited'
      },
      'formality': {
        'most_casual': 'Most Casual',
        'more_casual': 'More Casual',
        'casual': 'Casual',
        'professional': 'Professional',
        'more_professional': 'More Professional',
        'most_professional': 'Most Professional'
      },
      'urgency': {
        'most_relaxed': 'Most Relaxed',
        'more_relaxed': 'More Relaxed',
        'relaxed': 'Relaxed',
        'immediate': 'Immediate',
        'more_immediate': 'More Immediate',
        'most_immediate': 'Most Immediate'
      },
      'humor': {
        'most_humorous': 'Most Humorous',
        'more_humorous': 'More Humorous',
        'humorous': 'Humorous',
        'serious': 'Serious',
        'more_serious': 'More Serious',
        'most_serious': 'Most Serious'
      }
    };
    
    const modifierNameLower = modifierName.toLowerCase();
    const levelNameLower = levelName.toLowerCase();
    
    if (levelMappings[modifierNameLower] && levelMappings[modifierNameLower][levelNameLower]) {
      return levelMappings[modifierNameLower][levelNameLower];
    }
    
    return levelName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  const getNumericLevel = (modifierName, levelName) => {
    const levelMappings = {
      'assertiveness': {
        'most_collaborative': -3, 'more_collaborative': -2, 'collaborative': -1,
        'decisive': 1, 'more_decisive': 2, 'most_decisive': 3
      },
      'length': {
        'most_concise': -3, 'more_concise': -2, 'concise': -1,
        'detailed': 1, 'more_detailed': 2, 'most_detailed': 3
      },

      'directness': {
        'most_diplomatic': -3, 'more_diplomatic': -2, 'diplomatic': -1,
        'blunt': 1, 'more_blunt': 2, 'most_blunt': 3
      },
      'energy': {
        'most_calm': -3, 'more_calm': -2, 'calm': -1,
        'excited': 1, 'more_excited': 2, 'most_excited': 3
      },
      'formality': {
        'most_casual': -3, 'more_casual': -2, 'casual': -1,
        'professional': 1, 'more_professional': 2, 'most_professional': 3
      },
      'urgency': {
        'most_relaxed': -3, 'more_relaxed': -2, 'relaxed': -1,
        'immediate': 1, 'more_immediate': 2, 'most_immediate': 3
      },
      'humor': {
        'most_humorous': -3, 'more_humorous': -2, 'humorous': -1,
        'serious': 1, 'more_serious': 2, 'most_serious': 3
      }
    };
    
    const modifierNameLower = modifierName.toLowerCase();
    const levelNameLower = levelName.toLowerCase();
    
    if (levelMappings[modifierNameLower] && levelMappings[modifierNameLower][levelNameLower] !== undefined) {
      return levelMappings[modifierNameLower][levelNameLower];
    }
    
    return 0;
  };
  
  if (!item) return null;
  
  return (
    <BottomSheetModal
      isOpen={isOpen}
      onClose={handleCancel}
      title={`Customize ${item.name}`}
      maxHeight="90vh"
      showGripper={true}
    >
      <div className="edit-modal-content">
        <div className="form-group">
          <label className="form-label">Name</label>
          <input
            type="text"
            value={item.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className="form-input"
            disabled={isLoading}
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">Description</label>
          <input
            type="text"
            value={item.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            className="form-input"
            disabled={isLoading}
          />
        </div>
        
        {/* Special handling for modifiers - show all levels */}
        {item.type === 'modifier' && item.levels ? (
          <div className="modifier-levels-section">
            <label className="form-label">Level Prompts</label>
            <div className="modifier-levels-grid">
              {item.levels.map((level, index) => {
                const levelDisplayName = getLevelDisplayNameFromString(item.name, level.level_name);
                const currentPrompt = level.updated_prompt !== null && level.updated_prompt !== undefined ? level.updated_prompt : '';
                const numericLevel = getNumericLevel(item.name, level.level_name);
                const levelNumberText = numericLevel === 0 ? 'Level 0' : `Level ${numericLevel > 0 ? '+' : ''}${numericLevel}`;
                
                return (
                  <div key={level.id} className="modifier-level-item">
                    <div className="level-header">
                      <h4>{levelNumberText}: {levelDisplayName}</h4>
                    </div>
                    <div className="level-prompt-container">
                      <textarea
                        value={currentPrompt}
                        onChange={(e) => handleLevelChange(index, 'updated_prompt', e.target.value)}
                        className="form-textarea level-prompt"
                        placeholder={level.starter_prompt || "Enter the prompt for this level..."}
                        rows={4}
                        disabled={isLoading}
                      />
                      {level.updated_prompt && (
                        <button
                          onClick={() => handleLevelChange(index, 'updated_prompt', null)}
                          className="reset-prompt-btn"
                          title="Reset to default prompt"
                          disabled={isLoading}
                        >
                          Reset to Default
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* Regular prompt field for non-modifiers */
          <div className="form-group">
            <label className="form-label">Prompt</label>
            <textarea
              value={item.prompt !== null && item.prompt !== undefined ? item.prompt : ''}
              onChange={(e) => handleInputChange('prompt', e.target.value)}
              className="form-textarea"
              placeholder={item.originalPrompt || "Enter the prompt that powers this transformation..."}
              rows={6}
              disabled={isLoading}
            />
          </div>
        )}
        
        <div className="modal-actions">
          <button 
            onClick={handleCancel} 
            className="btn-secondary"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button 
            onClick={handleSave} 
            className="btn-primary"
            disabled={isLoading || !item.name.trim()}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </BottomSheetModal>
  );
};

export default EditModal; 